import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Zap, CheckCircle2 } from "lucide-react";
import { submitLead } from "@/lib/leads.functions";
import { captureUtmParams } from "@/lib/utm";
import { US_STATES_ALL } from "@/data/usStates";
import { classifyLeadFootprint, waitlistNotice } from "@/lib/lead-footprint";

const MAX_LTV = 0.8; // program max on eligible DSCR purchases (lending-guideline cap)

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const num = (s: string) => Number(s.replace(/[^0-9.]/g, "")) || 0;

/**
 * 30-second quick scenario capture: price + rent + state, then contact.
 * Routes through submitLead (CRM, Arive, leads@ email, borrower email).
 * Illustrative math only — no rates or approval claims.
 */
export function QuickQuoteWidget({ sourcePage, compact = false }: { sourcePage: string; compact?: boolean }) {
  const [price, setPrice] = useState("400000");
  const [rent, setRent] = useState("3200");
  const [state, setState] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const p = num(price);
  const r = num(rent);
  const maxLoan = p * MAX_LTV;
  const yieldPct = p > 0 ? ((r * 12) / p) * 100 : 0;
  const footprint = classifyLeadFootprint({ state, product: "dscr-loans" });
  const isWaitlistState = footprint.status === "waitlist";

  const mutation = useMutation({
    mutationFn: () =>
      submitLead({
        data: {
          name,
          email,
          phone,
          product: "dscr-loans",
          state,
          property_type: "Investment property",
          estimated_value: p || null,
          loan_amount: maxLoan || null,
          message: [
            `Quick scenario quote (${sourcePage})`,
            `Purchase price / value: ${money(p)}`,
            `Estimated monthly rent: ${money(r)}`,
            `Illustrative max loan at 80% LTV: ${money(maxLoan)}`,
            `Gross rent yield: ${yieldPct.toFixed(1)}%`,
          ].join("\n"),
          source_page: `quick-quote:${sourcePage}`,
          source_url: typeof window !== "undefined" ? window.location.href : "",
          ...captureUtmParams(),
        },
      }),
    onSuccess: (res: { leadId?: string | null; waitlisted?: boolean } | undefined) => {
      // Waitlist signups are not leads — never report them as conversions.
      if (res?.waitlisted) return;
      void import("@/lib/tracking").then((m) =>
        m.trackLeadConversion({ product: "dscr-loans", leadId: res?.leadId ?? undefined, form: "quick_quote" }),
      );
    },
  });

  const input = "w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm text-foreground";

  return (
    <aside
      data-inline-cta
      className="not-prose my-10 rounded-2xl border border-brand/30 bg-card p-6 shadow-md md:p-8"
      aria-label="Quick DSCR scenario quote"
    >
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand">
        <Zap className="h-4 w-4 text-brand-gold" aria-hidden /> 30-second scenario check
      </p>
      <h2 className="mt-2 font-display text-2xl font-bold text-foreground">
        See what your rental could support
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Enter three numbers. A licensed loan officer reviews your scenario and follows up with real options.
      </p>

      {mutation.isSuccess && (mutation.data as { waitlisted?: boolean } | undefined)?.waitlisted ? (
        <div className="mt-6 rounded-xl bg-muted p-5">
          <p className="flex items-center gap-2 font-semibold text-foreground">
            <CheckCircle2 className="h-5 w-5 text-brand" aria-hidden /> You're on the {footprint.stateName ?? "state"} waitlist.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            We don't arrange financing there yet, so no loan officer will contact you. Our guides and calculators are free to use anytime.
          </p>
        </div>
      ) : mutation.isSuccess ? (
        <div className="mt-6 rounded-xl bg-muted p-5">
          <p className="flex items-center gap-2 font-semibold text-foreground">
            <CheckCircle2 className="h-5 w-5 text-brand" aria-hidden /> Got it, {name.split(" ")[0]}. Your scenario is in.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Check your email for next steps. Want faster pricing? Add the property details now.
          </p>
          <Link
            to="/apply"
            search={{ product: "dscr-loans" } as never}
            className="mt-4 inline-block rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold text-primary hover:bg-brand-gold-hover"
          >
            Get Your Personalized Rate Quote
          </Link>
        </div>
      ) : (
        <form
          className="mt-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name || !email || !p || !r) return;
            mutation.mutate();
          }}
        >
          <div className={`grid gap-3 ${compact ? "sm:grid-cols-3" : "md:grid-cols-3"}`}>
            <label className="text-sm font-medium text-foreground">
              Price or value
              <input inputMode="numeric" className={`${input} mt-1`} value={price} onChange={(e) => setPrice(e.target.value)} required />
            </label>
            <label className="text-sm font-medium text-foreground">
              Monthly rent
              <input inputMode="numeric" className={`${input} mt-1`} value={rent} onChange={(e) => setRent(e.target.value)} required />
            </label>
            <label className="text-sm font-medium text-foreground">
              Property state
              <select className={`${input} mt-1`} value={state} onChange={(e) => setState(e.target.value)} required>
                <option value="">Select</option>
                {US_STATES_ALL.map((s) => (
                  <option key={s.abbrev} value={s.abbrev}>{s.name}</option>
                ))}
              </select>
            </label>
          </div>

          {isWaitlistState ? (
            <p role="status" className="rounded-lg border border-brand-gold/40 bg-brand-gold/10 p-3 text-sm text-foreground">
              {waitlistNotice(footprint.stateName)}
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted p-4 text-center">
            <div>
              <p className="text-xl font-black text-foreground">{money(maxLoan)}</p>
              <p className="text-xs text-muted-foreground">Illustrative loan at 80% LTV</p>
            </div>
            <div>
              <p className="text-xl font-black text-foreground">{yieldPct.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground">Gross rent yield</p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <input className={input} placeholder="Full name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
            <input className={input} type="email" placeholder="Email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <input className={input} type="tel" placeholder="Phone (for faster follow-up)" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          {mutation.error ? (
            <p className="text-sm text-destructive">Something went wrong. Please try again or call us.</p>
          ) : null}

          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full rounded-md bg-brand-gold px-5 py-3 text-base font-semibold text-primary hover:bg-brand-gold-hover disabled:opacity-50"
          >
            {mutation.isPending ? "Sending…" : isWaitlistState ? `Join the ${footprint.stateName} Waitlist` : "Get My Scenario Reviewed"}
          </button>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Illustrative only, not an offer of credit or a loan approval. 80% LTV is a program maximum on eligible
            business-purpose DSCR purchases; actual terms depend on credit, reserves, property, appraisal, and
            underwriting. Business-purpose investment property only. Simply Approved Mortgages LLC is a mortgage broker,
            not a direct lender. NMLS #2620881 · Branch NMLS #2688012.
          </p>
        </form>
      )}
    </aside>
  );
}
